import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IconArrowLeft, IconChevronDown } from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
    DropdownMenuRadioGroup, DropdownMenuRadioItem,
} from '../../../components/ui/dropdown-menu';
import printTemplateCategoryService from '../../../services/printTemplateCategoryService';
import printTemplateService from '../../../services/printTemplateService';
import PrintTemplateEditor from '../../../features/print-template-editor/PrintTemplateEditor';
import SplashScreen from '../../../features/print-template-editor/ui/SplashScreen';
import { FigSegment, figInputCls } from '../../../features/print-template-editor/ui/figma';
import { createEmptyDocument } from '../../../features/print-template-editor/schema/documentSchema';
import { compactDocument } from '../../../features/print-template-editor/schema/compactDocument';
import { usePrintEditorStore } from '../../../features/print-template-editor/state/usePrintEditorStore';
import { compressImageToThumbnail, cropImageToThumbnail } from '../../../features/print-template-editor/utils/thumbnailUpload';
import { exportCanvasThumbnail } from '../../../features/print-template-editor/export/exportCanvas';

function slugify(text) {
    return String(text || '')
        .toLowerCase()
        .trim()
        .replace(/[^\w\s-]/g, '')
        .replace(/[\s_-]+/g, '-')
        .replace(/^-+|-+$/g, '') || `template-${Date.now()}`;
}

/** Ensure API canvasJson (object or JSON string) becomes a usable document. */
function parseCanvasDocument(raw, fallbackWidth, fallbackHeight) {
    let doc = raw;
    if (typeof doc === 'string') {
        try {
            doc = JSON.parse(doc);
        } catch {
            doc = null;
        }
    }
    if (!doc || typeof doc !== 'object') {
        return createEmptyDocument({
            width: fallbackWidth || 1050,
            height: fallbackHeight || 600,
        });
    }
    const canvas = { ...(doc.canvas || {}) };
    if (!canvas.width && fallbackWidth) canvas.width = fallbackWidth;
    if (!canvas.height && fallbackHeight) canvas.height = fallbackHeight;
    return { ...doc, canvas };
}

export default function PrintTemplateFormPage() {
    const { id } = useParams();
    const { i18n } = useTranslation();
    const isBn = i18n.language === 'bn';
    const isNew = !id || id === 'new';
    const navigate = useNavigate();
    const markClean = usePrintEditorStore((s) => s.markClean);

    const [loading, setLoading] = useState(!isNew);
    const [saving, setSaving] = useState(false);
    const [title, setTitle] = useState('Untitled template');
    // headerLeft is memoised and defined above handleSave — bridge them with a ref so
    // committing the name doesn't re-create the header on every render.
    const commitTitleRef = useRef(null);
    const [slug, setSlug] = useState('');
    const [status, setStatus] = useState('draft');
    // Free templates skip credit deduction on export. Default paid (false).
    const [isFree, setIsFree] = useState(false);
    // The category is a row in PrintTemplateCategory, so the form holds its id.
    // '' means no category. Categories are MANAGED on the templates list page —
    // this panel only picks one, so there is no "add new" path here.
    const [categoryId, setCategoryId] = useState('');
    const [categories, setCategories] = useState([]);
    const [initialDoc, setInitialDoc] = useState(null);
    const [templateId, setTemplateId] = useState(isNew ? null : Number(id));
    const [loadKey, setLoadKey] = useState(0);
    const [thumbnailPreview, setThumbnailPreview] = useState(null);
    /**
     * The picture the thumbnail was cut from, kept at a higher resolution than the
     * saved thumbnail and only in memory — cropping always re-cuts THIS, so a
     * second crop never re-compresses a compressed JPEG and can widen the
     * rectangle back out. After a reload the saved thumbnail is all there is, so
     * it becomes the source and the crop starts fresh.
     */
    const [thumbnailSource, setThumbnailSource] = useState(null);
    const [thumbnailCrop, setThumbnailCrop] = useState(null);
    const [thumbCropping, setThumbCropping] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await printTemplateCategoryService.list();
                if (!cancelled) setCategories(Array.isArray(res?.data) ? res.data : []);
            } catch {
                // categories optional for editor boot
            }
        })();
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        if (isNew) {
            setInitialDoc(createEmptyDocument());
            setSlug(slugify(`print-template-${Date.now()}`));
            setTemplateId(null);
            setCategoryId('');
            setThumbnailPreview(null);
            setThumbnailSource(null);
            setThumbnailCrop(null);
            setLoadKey((k) => k + 1);
            setLoading(false);
            return;
        }
        let cancelled = false;
        setLoading(true);
        setInitialDoc(null);
        (async () => {
            try {
                const res = await printTemplateService.get(id);
                const tpl = res?.data;
                if (!tpl || cancelled) return;
                setTemplateId(tpl.id);
                setTitle(tpl.title || '');
                setSlug(tpl.slug || '');
                setStatus(tpl.status || 'draft');
                setIsFree(tpl.isFree === true);
                setCategoryId(tpl.categoryId ? String(tpl.categoryId) : '');
                setThumbnailPreview(tpl.thumbnail || null);
                setThumbnailSource(tpl.thumbnail || null);
                setThumbnailCrop(null);
                setInitialDoc(parseCanvasDocument(tpl.canvasJson, tpl.width, tpl.height));
                setLoadKey((k) => k + 1);
                // Keep the template's own category selectable even if the list
                // fetch lost the race, or the category is drafted.
                if (tpl.categoryRef) {
                    setCategories((prev) => (prev.some((c) => c.id === tpl.categoryRef.id)
                        ? prev
                        : [...prev, tpl.categoryRef]));
                }
            } catch (err) {
                notifications.show({ title: 'Error', message: err.message || 'Failed to load', color: 'red' });
                navigate('/admin/print-templates');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [id, isNew, navigate]);

    const headerLeft = useMemo(() => (
        <div className="flex items-center gap-0.5 min-w-0 flex-1">
            <button
                type="button"
                title="Back to templates"
                onClick={() => navigate('/admin/print-templates')}
                className="h-7 w-7 shrink-0 flex items-center justify-center rounded-[6px] text-black/60 dark:text-white/60 hover:bg-[#f5f5f5] dark:hover:bg-[#383838] transition-colors"
            >
                <IconArrowLeft size={15} />
            </button>
            <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                onKeyDown={(e) => {
                    // Enter/Escape leave the field; blur is what commits the name.
                    if (e.key === 'Enter' || e.key === 'Escape') {
                        e.preventDefault();
                        e.currentTarget.blur();
                    }
                    e.stopPropagation(); // don't let editor shortcuts see typing
                }}
                onBlur={() => commitTitleRef.current?.()}
                placeholder="Untitled template"
                className="flex-1 min-w-0 h-7 px-1.5 bg-transparent text-[13px] font-semibold text-black dark:text-white outline-none rounded-[5px] hover:bg-[#f5f5f5] dark:hover:bg-[#383838] focus:ring-1 focus:ring-[#0d99ff] transition-colors"
            />
        </div>
    ), [title, navigate]);


    /**
     * Unsaved page-level edits. The editor store only knows about the canvas, so
     * without this a renamed or re-categorised template would close without a
     * word — see useUnsavedExitGuard. The thumbnail is tracked as a flag rather
     * than by value: it is a base64 image, and comparing it every render is a lot
     * of string for one boolean.
     *
     * The baseline is STATE, not a ref: the fields land one render after the page
     * mounts, so the baseline is always taken a render late — and a ref updated
     * then re-renders nothing, which left the page reading as unsaved from the
     * moment it opened and warned on every refresh.
     */
    const [savedMeta, setSavedMeta] = useState(null);
    const [thumbDirty, setThumbDirty] = useState(false);
    const metaKey = JSON.stringify([title.trim(), slug, status, isFree, categoryId]);
    const metaDirty = savedMeta !== null && metaKey !== savedMeta;

    // Baseline: whatever the page opened with, per template.
    useEffect(() => {
        setSavedMeta(metaKey);
        setThumbDirty(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- re-baseline on load, not on typing
    }, [templateId, loadKey]);

    const metaLabelCls = 'block text-[11px] text-black/50 dark:text-white/50 mb-1';

    /**
     * A category's label in the language the admin is reading in: Bangla first
     * when the UI is Bangla and the row has a Bangla name, English otherwise —
     * and each falls back to the other rather than rendering blank.
     */
    const catName = (cat) => (
        (isBn ? (cat?.nameBn || cat?.name) : (cat?.name || cat?.nameBn)) || ''
    );
    const selectedCategory = categories.find((c) => String(c.id) === String(categoryId)) || null;

    const metaSlot = (
        <div className="flex flex-col gap-2.5">
            <div>
                <span className={metaLabelCls}>Slug</span>
                <input
                    value={slug}
                    onChange={(e) => setSlug(e.target.value)}
                    placeholder="template-slug"
                    title={slug}
                    className={`${figInputCls} font-mono`}
                />
            </div>
            <div>
                <span className={metaLabelCls}>Category</span>
                {/* The app's own dropdown rather than a native select: the list is
                    the same menu the templates page filters with, so a Bangla
                    name renders in the panel's font instead of the OS menu's. */}
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <button
                            type="button"
                            title={selectedCategory ? catName(selectedCategory) : 'No category'}
                            className="w-full h-7 pl-2 pr-1.5 flex items-center justify-between gap-1 text-[11px] leading-none rounded-[5px]
                                border border-transparent outline-none cursor-pointer
                                bg-[#f5f5f5] dark:bg-[#383838] text-black dark:text-white
                                hover:border-[#e6e6e6] dark:hover:border-[#555555]
                                focus:border-[#0d99ff] focus:ring-1 focus:ring-[#0d99ff] transition-colors
                                data-[state=open]:border-[#0d99ff] data-[state=open]:ring-1 data-[state=open]:ring-[#0d99ff]"
                        >
                            <span className="truncate">
                                {selectedCategory ? catName(selectedCategory) : 'No category'}
                            </span>
                            <IconChevronDown size={12} className="shrink-0 text-black/50 dark:text-white/50" />
                        </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                        align="start"
                        className="w-[var(--radix-dropdown-menu-trigger-width)] max-h-64 overflow-y-auto"
                        // Editor shortcuts listen on the window; without this,
                        // typing to jump through the menu also fires them.
                        onKeyDown={(e) => e.stopPropagation()}
                    >
                        <DropdownMenuRadioGroup
                            value={categoryId || ''}
                            onValueChange={(v) => setCategoryId(v)}
                        >
                            <DropdownMenuRadioItem value="" className="text-[12px] cursor-pointer">
                                No category
                            </DropdownMenuRadioItem>
                            {categories.map((c) => (
                                <DropdownMenuRadioItem
                                    key={c.id}
                                    value={String(c.id)}
                                    className="text-[12px] cursor-pointer"
                                >
                                    {catName(c)}
                                </DropdownMenuRadioItem>
                            ))}
                        </DropdownMenuRadioGroup>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
            <div>
                <span className={metaLabelCls}>Status</span>
                {/* Three fixed choices — cheaper to read as tabs than as a menu
                    that has to be opened to see what is currently set. */}
                <FigSegment
                    value={status}
                    onChange={setStatus}
                    options={[
                        { value: 'draft', label: 'Draft' },
                        { value: 'active', label: 'Active' },
                        { value: 'archived', label: 'Archived' },
                    ]}
                />
            </div>
            <div>
                {/* Free templates skip credit deduction on export. */}
                <span className={metaLabelCls}>Pricing</span>
                <FigSegment
                    value={isFree ? 'free' : 'paid'}
                    onChange={(v) => setIsFree(v === 'free')}
                    options={[
                        { value: 'paid', label: 'Paid' },
                        { value: 'free', label: 'Free' },
                    ]}
                />
            </div>
        </div>
    );

    const categoryLabel = selectedCategory ? catName(selectedCategory) : 'No category';
    const subtitle = `${categoryLabel} · ${status.charAt(0).toUpperCase()}${status.slice(1)}`;

    const handleThumbnailUpload = async (file) => {
        try {
            // Two sizes of the same picture: the one that gets saved, and a bigger
            // one kept in memory for the crop dialog to cut from.
            const source = await compressImageToThumbnail(file, { maxWidth: 1600, quality: 0.92 });
            const compressed = await compressImageToThumbnail(source, { maxWidth: 640, quality: 0.78 });
            setThumbnailSource(source);
            setThumbnailCrop(null); // a new picture, so the old rectangle means nothing
            setThumbnailPreview(compressed);
            setThumbDirty(true);
            notifications.show({ title: 'Thumbnail', message: 'Thumbnail ready — save to apply', color: 'green' });
        } catch (err) {
            notifications.show({ title: 'Error', message: err.message || 'Failed to read image', color: 'red' });
        }
    };

    const handleThumbnailDelete = () => {
        setThumbnailPreview(null);
        setThumbnailSource(null);
        setThumbnailCrop(null);
        setThumbDirty(true);
        notifications.show({ title: 'Thumbnail', message: 'Thumbnail removed — save to apply', color: 'blue' });
    };

    const handleThumbnailCrop = async ({ crop, rotation, ratioId, custom }) => {
        const source = thumbnailSource || thumbnailPreview;
        if (!source) return;
        setThumbCropping(true);
        try {
            const cropped = await cropImageToThumbnail(source, {
                crop,
                rotation,
                maxWidth: 640,
                quality: 0.82,
            });
            setThumbnailPreview(cropped);
            // The source is deliberately left alone: the next crop re-cuts the
            // original, so the admin can widen the rectangle back out.
            setThumbnailCrop({ crop, rotation, ratioId, custom });
            setThumbDirty(true);
            notifications.show({ title: 'Thumbnail', message: 'Crop applied — save to apply', color: 'green' });
        } catch (err) {
            notifications.show({ title: 'Error', message: err.message || 'Failed to crop image', color: 'red' });
        } finally {
            setThumbCropping(false);
        }
    };

    const handleThumbnailUseDesign = async (frameId = null) => {
        try {
            const doc = usePrintEditorStore.getState().document;
            const frame = (doc.frames || []).find((f) => f.id === frameId) || (doc.frames || [])[0];
            // Captured big, so a crop of the design stays sharp; the saved
            // thumbnail is the shrunk copy.
            const full = await exportCanvasThumbnail({
                maxWidth: 1600,
                quality: 0.92,
                document: doc,
                // Capture the frame the panel offered, not always the first one.
                frameId: frame?.id,
            });
            if (!full) throw new Error('Could not render design thumbnail');
            const thumb = await compressImageToThumbnail(full, { maxWidth: 640, quality: 0.78 });
            setThumbnailSource(full);
            setThumbnailCrop(null);
            setThumbnailPreview(thumb);
            setThumbDirty(true);
            notifications.show({
                title: 'Thumbnail',
                message: frame?.name
                    ? `${frame.name} captured as thumbnail — save to apply`
                    : 'Design captured as thumbnail — save to apply',
                color: 'green',
            });
        } catch (err) {
            notifications.show({
                title: 'Error',
                message: err.message || 'Failed to capture design as thumbnail',
                color: 'red',
            });
        }
    };

    const handleSave = async (doc, opts = {}) => {
        // Autosave runs silently: no toasts, and it never creates a new template —
        // it only updates one that has already been saved once.
        const isAuto = !!opts.autosave;
        if (!title.trim()) {
            if (!isAuto) notifications.show({ title: 'Error', message: 'Title is required', color: 'red' });
            return;
        }
        if (isAuto && !templateId) return;
        setSaving(true);
        try {
            const thumbnail = thumbnailPreview || null;

            // Only ever an existing row: categories are created and edited on
            // the templates list page, never from this panel.
            const nextCategoryId = categoryId ? Number(categoryId) : null;
            const payload = {
                title: title.trim(),
                slug: slugify(slug || title),
                status,
                isFree,
                categoryId: nextCategoryId,
                width: doc.canvas.width,
                height: doc.canvas.height,
                // Stored as differences from the element factories' defaults —
                // typically a fifth of the size, and the editor puts the defaults
                // back on load (schema/compactDocument).
                canvasJson: compactDocument(doc),
                thumbnail,
            };
            let saved;
            if (templateId) {
                const res = await printTemplateService.update(templateId, payload);
                saved = res?.data;
                if (!isAuto) notifications.show({ title: 'Saved', message: 'Template updated', color: 'green' });
            } else {
                const res = await printTemplateService.create(payload);
                saved = res?.data;
                setTemplateId(saved?.id);
                notifications.show({ title: 'Saved', message: 'Template created', color: 'green' });
                if (saved?.id) navigate(`/admin/print-templates/${saved.id}/edit`, { replace: true });
            }

            // The API lifts embedded images into R2 and hands back their URLs.
            // Taking those on board is what stops the next autosave from shipping
            // the same megabytes of base64 all over again.
            usePrintEditorStore.getState().adoptMediaUrls(doc.elements, saved?.canvasJson?.elements);
            if (thumbnail) {
                setThumbnailPreview(saved?.thumbnail || thumbnail);
            }
            setCategoryId(nextCategoryId ? String(nextCategoryId) : '');
            // The page's own fields are saved too — re-baseline them, or the exit
            // guard keeps insisting there is unsaved work.
            // The slug is normalised on the way out, so show what was actually
            // stored rather than leaving the field looking unsaved.
            setSlug(payload.slug);
            setSavedMeta(JSON.stringify([
                payload.title, payload.slug, status, isFree, nextCategoryId ? String(nextCategoryId) : '',
            ]));
            setThumbDirty(false);
            markClean();
        } catch (err) {
            if (!isAuto) notifications.show({ title: 'Error', message: err.message || 'Failed to save', color: 'red' });
            else console.warn('[print-editor] Autosave failed:', err?.message || err);
        } finally {
            setSaving(false);
        }
    };

    // The editor's autosave watches the canvas document, but the title lives in page
    // state — so a name edit alone would never persist. Commit it on Enter/blur.
    commitTitleRef.current = () => {
        if (!templateId) return; // a name edit must never auto-create a template
        handleSave(usePrintEditorStore.getState().document, { autosave: true });
    };

    if (loading || !initialDoc) {
        return <SplashScreen persistent />;
    }

    return (
        <div className="h-dvh w-full">
            <PrintTemplateEditor
                key={`admin-editor-${templateId || 'new'}-${loadKey}`}
                initialDocument={initialDoc}
                mode="admin"
                onSave={handleSave}
                onAutosave={(doc) => handleSave(doc, { autosave: true })}
                saving={saving}
                hostDirty={metaDirty || thumbDirty}
                headerLeft={headerLeft}
                metaSlot={metaSlot}
                subtitle={subtitle}
                thumbnailPreview={thumbnailPreview}
                thumbnailSource={thumbnailSource}
                thumbnailCrop={thumbnailCrop}
                onThumbnailUpload={handleThumbnailUpload}
                onThumbnailDelete={handleThumbnailDelete}
                onThumbnailUseDesign={handleThumbnailUseDesign}
                onThumbnailCrop={handleThumbnailCrop}
                thumbnailCropping={thumbCropping}
            />
        </div>
    );
}
